import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { CheckCircle, Phone, Zap, Shield, Clock, Award } from 'lucide-react';
import { generators } from '../data/mock';

const GeneratorInstallation = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Briggs & Stratton Generator Installation
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Expert installation of home standby generators for reliable backup power. Never worry about power outages again with automatic whole-house backup systems.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Generator */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_next-day-electric/artifacts/dm6s54qz_Capture.PNG"
                alt="Generator Installation"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Install a Home Standby Generator?
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                Power outages can happen anytime due to storms, equipment failures, or grid overloads. A home standby generator automatically provides backup power to keep your home running smoothly.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Automatic Operation</h3>
                    <p className="text-gray-600">Starts automatically within seconds of a power outage</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Whole-House Power</h3>
                    <p className="text-gray-600">Powers essential appliances and entire home systems</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Peace of Mind</h3>
                    <p className="text-gray-600">Protect your family and home investment during outages</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Increase Home Value</h3>
                    <p className="text-gray-600">Adds value and appeal to your property</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Briggs & Stratton */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Briggs & Stratton?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trusted brand with over 100 years of engine innovation and reliability
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Powerful Performance</h3>
              <p className="text-gray-600">
                Advanced engine technology for reliable, quiet operation
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Durable Design</h3>
              <p className="text-gray-600">
                Corrosion-resistant enclosures for long-lasting outdoor use
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Multi-Fuel Options</h3>
              <p className="text-gray-600">
                Run on natural gas or liquid propane for flexibility
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Easy Maintenance</h3>
              <p className="text-gray-600">
                Tool-less access and eco-friendly weekly test mode
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Generator Models */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Briggs & Stratton PowerProtect Models
            </h2>
            <p className="text-xl text-gray-600">
              Choose the right generator for your home's power needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {generators.map((gen) => (
              <Card key={gen.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl">{gen.model}</CardTitle>
                  <CardDescription className="text-xl font-semibold text-blue-900">
                    {gen.power}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Key Features:</h4>
                    <ul className="space-y-2">
                      {gen.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900 mb-1">Ideal For:</p>
                    <p className="text-gray-600">{gen.ideal}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Installation Process
            </h2>
            <p className="text-xl text-gray-600">
              Professional installation from start to finish
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Consultation</h3>
              <p className="text-gray-600">
                We assess your power needs and recommend the ideal generator size
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Site Preparation</h3>
              <p className="text-gray-600">
                Level pad installation and proper clearance for ventilation
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Installation</h3>
              <p className="text-gray-600">
                Professional wiring, gas connection, and automatic transfer switch setup
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">4</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Testing & Training</h3>
              <p className="text-gray-600">
                System testing and operation training for your peace of mind
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Warranty & Service */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Comprehensive Warranty & Service
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Briggs & Stratton generators come with industry-leading warranties, typically ranging from 3-5 years on parts and labor depending on the model. Our team provides ongoing maintenance and support to keep your generator running at peak performance.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <Award className="h-12 w-12 text-blue-900 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Extended Warranty</h3>
                <p className="text-gray-600">Up to 5 years parts & labor coverage</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <Clock className="h-12 w-12 text-blue-900 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Annual Maintenance</h3>
                <p className="text-gray-600">Scheduled service to ensure reliability</p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <Shield className="h-12 w-12 text-blue-900 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">24/7 Support</h3>
                <p className="text-gray-600">Emergency service when you need it most</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready for Reliable Backup Power?
          </h2>
          <p className="text-xl text-gray-800 mb-8">
            Contact us today for a free consultation and quote on Briggs & Stratton generator installation
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-blue-900 hover:bg-blue-800 text-white">
              <Link to="/contact">Get Free Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GeneratorInstallation;